/**
 * A8: when the server answers 403 { code: 'step_up_required' }, ask the user to
 * confirm it's them, then retry the original request once.
 *
 * The prompt module is loaded on demand (dynamic import) so this file has no
 * runtime dependency on the API client or React (no import cycle with axiosConfig).
 */
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export type StepUpMethod = 'totp' | 'password';
export type StepUpRequester = (method: StepUpMethod) => Promise<boolean>;

const defaultRequester: StepUpRequester = async (method) => {
  const { requestStepUp } = await import('../services/stepUpPrompt');
  return requestStepUp(method);
};

export function isStepUpRequired(error: AxiosError): boolean {
  const data = error.response?.data as { code?: string } | undefined;
  return error.response?.status === 403 && data?.code === 'step_up_required';
}

export function installStepUpInterceptor(instance: AxiosInstance, requester: StepUpRequester = defaultRequester): number {
  return instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _stepUpRetried?: boolean }) | undefined;
    if (config && !config._stepUpRetried && isStepUpRequired(error)) {
      const method = ((error.response?.data as { method?: StepUpMethod })?.method) ?? 'password';
      const confirmed = await requester(method);
      if (confirmed) {
        config._stepUpRetried = true;
        return instance(config);
      }
    }
    return Promise.reject(error);
  });
}
